import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { sanitizeSearchQuery } from '@/utils/sanitize';
import { geocodeAddress } from '@/utils/geocodeAddress';
import { normalizePhoneE164 } from '@/utils/phone';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { parseDateInTz } from '@/utils/timezone';
import type { LeadWithRelations } from '@/types';
import type { LeadFormData } from '@/schemas/lead.schema';

// Wraps a raw Supabase PostgrestError (plain object) into a proper JS Error
// so Sentry receives a real stack trace instead of "Object captured as exception"
function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'message' in err) {
    const pgErr = err as { message: string; code?: string; details?: string; hint?: string };
    const e = new Error(`[DB ${pgErr.code ?? 'error'}] ${pgErr.message}`);
    e.name = 'PostgrestError';
    (e as any).details = pgErr.details;
    (e as any).hint = pgErr.hint;
    (e as any).code = pgErr.code;
    return e;
  }
  return new Error(String(err));
}

export function useLeads(statusFilter?: string, searchQuery?: string) {
  // Realtime subscriptions now handled by RealtimeProvider

  return useQuery({
    queryKey: ['leads', statusFilter, searchQuery],
    queryFn: async () => {
      let query = supabase
        .from('leads')
        .select(`
          *,
          service_types(name),
          lead_sources(name),
          campaigns(name, external_campaign_id),
          assigned_csr:profiles!leads_assigned_csr_id_fkey(full_name),
          lead_owner:profiles!leads_lead_owner_id_fkey(full_name),
          assigned_tech:techs!leads_assigned_tech_id_fkey(name),
          customers(id, customer_name, total_jobs_count, total_leads_count, total_lifetime_value)
        `)
        .order('created_at', { ascending: false });

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (searchQuery) {
        const sanitizedQuery = sanitizeSearchQuery(searchQuery);
        if (sanitizedQuery.length > 0) {
          query = query
            .or(`customer_name.ilike.%${sanitizedQuery}%,customer_phone.ilike.%${sanitizedQuery}%,lead_number.ilike.%${sanitizedQuery}%`)
            .limit(50);
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      // Transform joins to match expected interface
      const transformedData = (data || []).map((lead: any) => ({
        ...lead,
        csr_profile: Array.isArray(lead.assigned_csr) ? lead.assigned_csr[0] : lead.assigned_csr,
        tech_profile: Array.isArray(lead.assigned_tech) ? lead.assigned_tech[0] : lead.assigned_tech,
        owner_profile: Array.isArray(lead.lead_owner) ? lead.lead_owner[0] : lead.lead_owner,
      }));

      return transformedData as LeadWithRelations[];
    },
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 300000,   // Keep in cache for 5 minutes
    placeholderData: keepPreviousData,
  });
}

export function useLead(leadId: string | null) {
  return useQuery({
    queryKey: ['lead', leadId],
    queryFn: async () => {
      if (!leadId) return null;

      const { data, error } = await supabase
        .from('leads')
        .select(`
          *,
          service_types(name),
          lead_sources(name),
          campaigns(name, external_campaign_id),
          assigned_csr:profiles!leads_assigned_csr_id_fkey(full_name),
          lead_owner:profiles!leads_lead_owner_id_fkey(full_name),
          assigned_tech:techs!leads_assigned_tech_id_fkey(name),
          customers(id, customer_name, total_jobs_count, total_leads_count, total_lifetime_value)
        `)
        .eq('id', leadId)
        .single();

      if (error) throw error;

      // Transform joins to match expected interface
      const anyData = data as any;
      const transformed = {
        ...data,
        csr_profile: Array.isArray(anyData.assigned_csr) ? anyData.assigned_csr[0] : anyData.assigned_csr,
        tech_profile: Array.isArray(anyData.assigned_tech) ? anyData.assigned_tech[0] : anyData.assigned_tech,
        owner_profile: Array.isArray(anyData.lead_owner) ? anyData.lead_owner[0] : anyData.lead_owner,
      };

      return transformed as unknown as LeadWithRelations;
    },
    enabled: !!leadId,
    staleTime: 30000,
    gcTime: 300000,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  const { locale, timezone } = useCompanyLocale();

  return useMutation({
    mutationFn: async ({ data, userId, customerId }: { data: LeadFormData; userId: string; customerId?: string | null }) => {
      // Geocode fallback: if CSR typed an address without picking from autocomplete,
      // attempt to geocode before insert so the metro trigger has coords to match on.
      let lat = data.lat ?? null;
      let lng = data.lng ?? null;
      if (data.customer_address && (lat == null || lng == null)) {
        const coords = await geocodeAddress(data.customer_address, locale.countryCode);
        if (coords) {
          lat = coords.lat;
          lng = coords.lng;
        }
      }

      const leadData: any = {
        customer_type: data.customer_type || 'individual',
        customer_name: data.customer_name,
        company_name: data.company_name || null,
        // E.164 so phone_last9 matching works in every market (NZ especially)
        customer_phone: normalizePhoneE164(data.customer_phone, locale),
        customer_email: data.customer_email || null,
        customer_address: data.customer_address,
        customer_suburb: data.customer_suburb || null,
        customer_state: data.customer_state || null,
        customer_postcode: data.customer_postcode || null,
        lat,
        lng,
        issue_description: data.issue_description,
        service_type_id: data.service_type_id || null,
        lead_source_id: data.lead_source_id || null,
        campaign_id: data.campaign_id || null,
        quoted_price: data.quoted_price ? parseFloat(data.quoted_price) : null,
        // Midnight in the company timezone; a bare date would be stored as midnight UTC.
        scheduled_date: data.scheduled_date ? parseDateInTz(data.scheduled_date, timezone).toISOString() : null,
        scheduled_time: data.scheduled_time || null,
        notes: data.notes || null,
        assigned_tech_id: data.assigned_tech_id || null,
        created_by: userId,
        assigned_csr_id: data.assigned_csr_id || null,
        lead_owner_id: userId,
        status: 'new',
        lead_entry_type: 'manual',
      };
      if (customerId) leadData.customer_id = customerId;
      const { data: created, error } = await supabase.from('leads').insert([leadData]).select('id').single();
      if (error) {
        // Raised by the zz_lead_phone_guards trigger: same phone, non-lost lead in the last 24h.
        const m = /DUPLICATE_LEAD_24H\|([^|]+)\|/.exec(error.message ?? '');
        if (m) {
          throw new Error(`This phone number already has lead ${m[1]} from the last 24 hours. Open that lead instead of creating another.`);
        }
        throw toError(error);
      }
      return created as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ title: 'Lead created successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create lead',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, status }: { leadId: string; status: string }) => {
      const { error } = await supabase
        .from('leads')
        .update({ status })
        .eq('id', leadId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast({ title: 'Lead status updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Legacy conversion hook - still works but uses old function
export function useConvertLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, techId }: { leadId: string; techId: string }) => {
      const { data: jobId, error } = await supabase.rpc('convert_lead_to_job', {
        _lead_id: leadId,
        _tech_id: techId,
      });
      if (error) throw toError(error);
      return jobId;
    },
    onSuccess: (jobId) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });

      // Fetch the job to get the job number
      supabase
        .from('jobs')
        .select('job_number')
        .eq('id', jobId)
        .single()
        .then(({ data }) => {
          toast({
            title: 'Lead converted successfully',
            description: data?.job_number ? `Job ${data.job_number} has been created` : 'Job has been created'
          });
        });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to convert lead',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// New V2 conversion hook with override support
export function useConvertLeadV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      techId,
      overrideData,
      isRecall,
    }: {
      leadId: string;
      techId: string;
      isRecall?: boolean;
      overrideData?: {
        customer_name?: string;
        customer_phone?: string;
        customer_email?: string;
        customer_address?: string;
        customer_type?: string;
        company_name?: string;
        service_type_id?: string;
        issue_description?: string;
        notes?: string;
        scheduled_date?: string;
        scheduled_time?: string;
        scheduled_time_start?: string;
        scheduled_time_end?: string;
        quoted_price?: number;
      };
    }) => {
      const { data, error } = await supabase.rpc('convert_lead_to_job_v2', {
        _lead_id: leadId,
        _tech_id: techId,
        _override_data: overrideData || null,
      });

      if (error) throw toError(error);

      // The RPC's insert columns are fixed, so the recall flag is applied
      // in a follow-up update. Non-fatal: the job exists either way.
      if (isRecall) {
        const result = Array.isArray(data) ? data[0] : data;
        if (result?.job_id) {
          const { error: recallError } = await supabase
            .from('jobs')
            .update({ is_recall: true })
            .eq('id', result.job_id);
          if (recallError) {
            console.warn('Failed to set recall flag on new job:', recallError);
            toast({
              title: 'Recall flag not saved',
              description: 'The job was created, but marking it as a recall failed. You can set it from the job details.',
              variant: 'destructive',
            });
          }
        }
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });

      const result = Array.isArray(data) ? data[0] : data;
      // The RPC's past-date check compares UTC dates, so it misfires for company-midnight
      // timestamps; the wizard warns about past dates in the company timezone instead.
      const warnings = (Array.isArray(result?.warnings) ? result.warnings : [])
        .filter((w: { type?: string } | null) => w?.type !== 'past_scheduled_date');

      let description = `Job ${result?.job_number || ''} created successfully`;

      if (warnings.length > 0) {
        const warningMessages = warnings.map((w: any) => w.message).join(', ');
        description += `. Warnings: ${warningMessages}`;
      }

      toast({
        title: 'Lead converted to job',
        description,
      });
    },
    onError: (error: Error, variables) => {
      if (/already converted|already has an active job/i.test(error.message)) {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        toast({
          title: 'Already booked',
          description: 'This lead has already been booked as a job.',
        });
        return;
      }
      toast({
        title: 'Failed to convert lead',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useClaimLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, userId }: { leadId: string; userId: string }) => {
      const { error } = await supabase
        .from('leads')
        .update({ assigned_csr_id: userId, updated_at: new Date().toISOString() })
        .eq('id', leadId)
        .is('assigned_csr_id', null); // Only claim if still unassigned (race-condition guard)

      if (error) throw toError(error);

      // Log the claim as an assignment activity
      const { error: activityError } = await supabase.from('lead_activities').insert({
        lead_id: leadId,
        user_id: userId,
        activity_type: 'assignment',
        description: 'Lead claimed from Unassigned Pool',
        metadata: { claim_type: 'manual_claim' },
      } as any);

      if (activityError) {
        console.warn('Could not log claim activity (non-fatal):', activityError.message);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      toast({ title: 'Lead claimed', description: 'This lead is now assigned to you.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to claim lead',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) throw toError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({
        title: 'Lead deleted',
        description: 'Lead has been permanently deleted.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete lead',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
